import { observable, action, runInAction } from "mobx";
import agent from "../agent";
import moment from "moment"
import { Promise } from "bluebird";
import { Storage } from "aws-amplify";
import CacheHelper from '../helper/cache';

const API_ROOT = process.env.REACT_APP_MAIN_SERVICE_URL;

class GigPromotionStore{

    @observable pendingGigPromotions = [];
    @observable pendingTotal = null;
    @observable publishedGigPromotions = [];
    @observable publishedTotal = null;
    @observable completedGigPromotions = [];
    @observable completedTotal = null;

    @observable actImages = [];
    @observable error = {};
    @observable gigPromotion = {
        title: undefined,
        tempTitle: "",
        actId: undefined,
        actName: "",
        venueId: undefined,
        venueName: "",
        venue: undefined,
        dateStart: new Date(),
        timeStart: new Date(),
        timeEnd: new Date(),
        ticketUrl: "",
        ticketPrice: "",
        actGenres: undefined,
        actVideoUrl: undefined,
        actDescription: undefined,
        gigImage: undefined,
        isDraft: false,
        published: false,
        venueContact: "",
        venueAddress: "",
        venuePhone: "",
        venueEmail: ""
    }

    @action
    resetGigPromotion(){
        this.actImages = [];
        this.error = {};
        this.gigPromotion = {
            title: undefined,
            tempTitle: "",
            actId: undefined,
            actName: "",
            venueId: undefined,
            venueName: "",
            venue: undefined,
            dateStart: new Date(),
            timeStart: new Date(),
            timeEnd: new Date(),
            ticketUrl: "",
            ticketPrice: "",
            actGenres: undefined,
            actVideoUrl: undefined,
            actDescription: undefined,
            gigImage: undefined,
            isDraft: false,
            published: false,
            venueContact: "",
            venueAddress: "",
            venuePhone: "",
            venueEmail: "",
            actGenres: undefined,
            actVideoUrl: undefined,
        }
    }

    @action
    getGigPromotionDetail(id){
        this.actImages = [];
        return agent.GigPromotion.getById(id).then(action(
            async response => {
                // Resolve image URLs BEFORE assigning to the observable store.
                // MobX 4 only tracks properties that exist on an object at the
                // moment it becomes observable (i.e. when this.gigPromotion = response
                // is executed). Setting gigImage afterwards on the already-observable
                // object adds a non-tracked property that never triggers re-renders.
                // Pre-populating it here ensures MobX observes it from the start.

                const imageUrl = await CacheHelper.getImgLink(response.imageUrl);
                if (response.gigImageId) {
                    response.gigImage = { id: response.gigImageId, url: imageUrl };
                }

                const actImageData = [];
                if (response.gigActImageIds && Array.isArray(response.gigActImages)) {
                    for (const actImage of response.gigActImages) {
                        const url = await CacheHelper.getImgLink(actImage.url);
                        actImageData.push({ id: parseInt(actImage.id, 10), url });
                    }
                }

                const v = response.venue;
                response.tempTitle = response.title;
                response.venueContact = v?.contactName;
                response.venueAddress = v
                    ? `${v.street ? v.street + ", " : ""}${v.city ? v.city + ", " : ""}${v.state ? v.state : ""}`.replace(/,\s*$/, "")
                    : "";
                response.venuePhone = v?.contactPhone;
                response.venueEmail = v?.email;

                runInAction(() => {
                    this.gigPromotion = response;
                    this.actImages = actImageData;
                });
            }
        ))
    }

    @action
    setGigPromotion(key, value)
    {
        this.gigPromotion[key] = value;
    }
    
    @action
    addActImages(images){
        const MAX_IMAGES = 6;
        if( (images.length + this.actImages.length) <= MAX_IMAGES){
            images.forEach(image => {
                image.url = image.preview;
                this.actImages.push(image);
            })
        }
    }

    @action
    deleteActImages(image){
        if(image.id){
            return agent.GigPromotion.deleteGigPromotionImage(image.id).then(
                action(() => {
                    this.actImages = this.actImages.filter(x => x !== image);
                    return this.actImages;
                })
            );
        }
        else{
            this.actImages = this.actImages.filter(x => x !== image);
            return Promise.resolve(this.actImages);
        }
    }

    @action
    removeGigPromotionImage(imageId){
        if(imageId){
            return agent.GigPromotion.deleteGigPromotionImage(imageId).then( () => {
                this.gigPromotion.gigImage = undefined;
                return Promise.resolve();
            })
        }
        else{
            return Promise.resolve();
        }
    }
    
    @action
    resetActImages(){
        this.actImages = [];
    }

    @action
    saveGigPromotion(){
        if (!this.actImages || this.actImages.length === 0) {
            this.error = { actImages: "At least one act image is required." };
            return Promise.reject(new Error("At least one act image is required."));
        }
        this.error = {};
        // utcOffset uses JS convention (negative of standard): -600 = UTC+10 (Brisbane).
        // Standard offset = -utcOffset minutes. End of day in user's timezone = UTC end-of-day minus that offset.
        const saveUtcOffset = this.gigPromotion.utcOffset != null ? this.gigPromotion.utcOffset : -600;
        const data = {
            isDraft: this.gigPromotion.isDraft,
            published: this.gigPromotion.published,
            ticketUrl: this.gigPromotion.ticketUrl,
            ticketPrice: this.gigPromotion.ticketPrice,
            actDescription: this.gigPromotion.actDescription,
            timeStart: moment(this.gigPromotion.timeStart, "HH:mm")
                        .local()
                        .format("HH:mm"),
            timeEnd: this.gigPromotion.timeEnd
                        ? moment(this.gigPromotion.timeEnd, "HH:mm").local().format("HH:mm")
                        : null,
            dateStart: moment.utc(this.gigPromotion.dateStart).endOf("day").subtract(-saveUtcOffset, "minutes"),
            venueId: this.gigPromotion.venueId,
            actId: this.gigPromotion.actId,
            actName: this.gigPromotion.actName,
            actVideoUrl: this.gigPromotion.actVideoUrl,
            actGenres: this.gigPromotion.actGenres,
            id: this.gigPromotion.id,
            title: this.gigPromotion.title,
            utcOffset: this.gigPromotion.utcOffset != null ? this.gigPromotion.utcOffset : -600
        }
        
        return agent.GigPromotion.saveGigPromotion(data).then( response => {
            if(this.gigPromotion.gigImage || this.actImages){
                return Promise.all([
                    this.gigPromotion.gigImage ? this.setGigPromotionImage(response.id) : undefined,
                    this.actImages ? this.setGigPromotionActImage(response.id) : undefined
                ]).then((response) => {
                    return Promise.resolve();
                }, () => {
                    return Promise.resolve();
                })
            }
        })
    }

    @action
    async setGigPromotionImage(gigPromotionId) {
        var image = this.gigPromotion.gigImage;
        console.log(image)
        const fileExtension = image.name.split('.').pop();
        var ticks = (((new Date()).getTime() * 10000) + 621355968000000000);
        var name = `${ticks}.${fileExtension}`;
        var result = await this.uploadGigPromotionImage(gigPromotionId, image, name);
        var gigImage = {
            url: result.key,
            filename: name,
            mime: image.type
        }
     
        var uploadedImage = await agent.GigPromotion.uploadGigPromotionImage(gigPromotionId, gigImage);
        return uploadedImage
        
    }

    @action
    async setGigPromotionActImage(gigPromotionId) {

        var images = this.actImages;
        let gigImages = [];
        for (const image of images) {
            const fileExtension = image.name.split('.').pop();
            var ticks = (((new Date()).getTime() * 10000) + 621355968000000000);
            var name = `${ticks}.${fileExtension}`;
            var result = await this.uploadGigPromotionActImage(gigPromotionId, image, name);
            var gigImage = {
                url: result.key,
                filename: name,
                mime: image.type
            }
            gigImages.push(gigImage);
        }

        var uploadedImage = gigImages.length > 0 ? await agent.GigPromotion.uploadGigPromotionActImage(gigPromotionId, gigImages) : null;
        return uploadedImage
        
    }

    async uploadGigPromotionActImage(id, profileImage, name) {
        try {
            const result = await Storage.put(
                `promotion/gig/act/image/${id}/${name}`,
                profileImage
            );
            return result;
        } catch (exception) {
            // console.log(exception);
        }
    }

    async uploadGigPromotionImage(id, profileImage, name) {
        try {
            const result = await Storage.put(
                `promotion/gig/image/${id}/${name}`,
                profileImage
            );
            return result;
        } catch (exception) {
            // console.log(exception);
        }
    }

    @action
    removeGigPromotion(gigPromotionId){
        return agent.GigPromotion.removeGigPromotion(gigPromotionId).then(action(
            response => {
                console.log(response);
                this.pendingGigPromotions = this.pendingGigPromotions.filter(x => x.id !== response.id);
                this.publishedGigPromotions = this.publishedGigPromotions.filter(x => x.id !== response.id);
                this.completedGigPromotions = this.completedGigPromotions.filter(x => x.id !== response.id);
                return Promise.resolve();
            }
        ))
    }

    @action
    getPendingGigPromotion(){
        return agent.GigPromotion.getPending().then(action(
            async response => {
                for (const gigPromotion of response) {
                    gigPromotion.imageUrl = await CacheHelper.getImgLink(gigPromotion.imageUrl)
                }
                runInAction(() => {
                    this.pendingGigPromotions = response;
                })
                return this.pendingGigPromotions;
            }
        ))
    }

    @action
    getPublishedGigPromotion(){
        return agent.GigPromotion.getPublished().then(action(
            async response => {
                for (const gigPromotion of response) {
                    gigPromotion.imageUrl = await CacheHelper.getImgLink(gigPromotion.imageUrl)
                }
                runInAction(() => {
                    this.publishedGigPromotions = response;
                })
                return this.publishedGigPromotions ;
            }
        ))
    }

    @action
    getCompletedGigPromotion(){
        return agent.GigPromotion.getCompleted().then(action(
            async response => {
                for (const gigPromotion of response) {
                    gigPromotion.imageUrl = await CacheHelper.getImgLink(gigPromotion.imageUrl)
                }
                runInAction(() => {
                    this.completedGigPromotions = response;
                })
                return this.completedGigPromotions;
            }
        ))
    }

    // Helper: normalise API response that may be an array or { data, total }
    _parseListResponse(response) {
        if (Array.isArray(response)) {
            return { data: response, total: null };
        }
        const data = Array.isArray(response.data) ? response.data : [];
        const total = typeof response.total === "number" ? response.total : null;
        return { data, total };
    }

    @action
    async getPendingFiltered({ dateFrom, dateTo, venueIds, page, take, search }) {
        const raw = await agent.GigPromotion.getPendingFiltered(dateFrom, dateTo, venueIds, page, take, search);
        const { data, total } = this._parseListResponse(raw);
        for (const gp of data) {
            gp.imageUrl = await CacheHelper.getImgLink(gp.imageUrl);
        }
        runInAction(() => {
            this.pendingGigPromotions = data;
            this.pendingTotal = total;
        });
        return { data: this.pendingGigPromotions, total };
    }

    @action
    async getPublishedFiltered({ dateFrom, dateTo, venueIds, page, take, search }) {
        const raw = await agent.GigPromotion.getPublishedFiltered(dateFrom, dateTo, venueIds, page, take, search);
        const { data, total } = this._parseListResponse(raw);
        for (const gp of data) {
            gp.imageUrl = await CacheHelper.getImgLink(gp.imageUrl);
        }
        runInAction(() => {
            this.publishedGigPromotions = data;
            this.publishedTotal = total;
        });
        return { data: this.publishedGigPromotions, total };
    }

    @action
    async getCompletedFiltered({ dateFrom, dateTo, venueIds, page, take, search }) {
        const raw = await agent.GigPromotion.getCompletedFiltered(dateFrom, dateTo, venueIds, page, take, search);
        const { data, total } = this._parseListResponse(raw);
        for (const gp of data) {
            gp.imageUrl = await CacheHelper.getImgLink(gp.imageUrl);
        }
        runInAction(() => {
            this.completedGigPromotions = data;
            this.completedTotal = total;
        });
        return { data: this.completedGigPromotions, total };
    }

    @action
    patchGigPromotionTimeEnd(gigPromotion, timeEnd) {
        const patchUtcOffset = gigPromotion.utcOffset != null ? gigPromotion.utcOffset : -600;
        const data = {
            isDraft: gigPromotion.isDraft,
            published: gigPromotion.published,
            ticketUrl: gigPromotion.ticketUrl,
            ticketPrice: gigPromotion.ticketPrice,
            actDescription: gigPromotion.actDescription,
            timeStart: moment(gigPromotion.timeStart, "HH:mm").local().format("HH:mm"),
            timeEnd: moment(timeEnd, "HH:mm").local().format("HH:mm"),
            dateStart: moment.utc(gigPromotion.dateStart).endOf("day").subtract(-patchUtcOffset, "minutes"),
            venueId: gigPromotion.venueId,
            actId: gigPromotion.actId,
            actName: gigPromotion.actName,
            actVideoUrl: gigPromotion.actVideoUrl,
            actGenres: gigPromotion.actGenres,
            id: gigPromotion.id,
            title: gigPromotion.title,
            utcOffset: gigPromotion.utcOffset != null ? gigPromotion.utcOffset : -600
        };
        return agent.GigPromotion.saveGigPromotion(data).then(action(() => {
            const lists = [this.pendingGigPromotions, this.publishedGigPromotions, this.completedGigPromotions];
            for (const list of lists) {
                const match = list.find(x => x.id === gigPromotion.id);
                if (match) {
                    match.timeEnd = timeEnd;
                }
            }
        }));
    }

    @action
    discoverOztixVenues(params) {
        return agent.GigPromotion.getOztixVenues(params).then(action(response => {
            if (Array.isArray(response)) return response;
            if (response && Array.isArray(response.response)) return response.response;
            return [];
        }));
    }

    @action
    previewOztixEvents(params) {
        return agent.GigPromotion.getOztixEvents(params).then(action(response => {
            if (Array.isArray(response)) return response;
            if (response && Array.isArray(response.response)) return response.response;
            return [];
        }));
    }

    @action
    syncFromOztix(payload) {
        return agent.GigPromotion.syncFromOztix(payload).then(action(response => {
            return response;
        }));
    }

    @action
    async loadOrganisationEmbedGigs(organisationId, params) {
        const response = await agent.GigPromotion.getOrganisationEmbedGigs(organisationId, params);
        let gigs = [];
        if (Array.isArray(response)) gigs = response;
        else if (response && Array.isArray(response.response)) gigs = response.response;

        for (const gig of gigs) {
            gig.resolvedImageUrl = await CacheHelper.getImgLink(gig.imageUrl);

            if (gig.act && gig.act.profileImage) {
                const pi = gig.act.profileImage;
                gig.act.resolvedProfileImage = await CacheHelper.getImgLink(
                    pi.urlOriginal || pi.url400 || pi.url200
                );
            }

            if (gig.venue && gig.venue.backgroundImages && gig.venue.backgroundImages.length > 0) {
                const bi = gig.venue.backgroundImages[0];
                gig.venue.resolvedBackgroundImage = await CacheHelper.getImgLink(
                    bi.urlOriginal || bi.url960 || bi.url
                );
            }
        }

        return gigs;
    }
}

export default new GigPromotionStore();